/**
 * ERC-7715 payload builder + response decoder for the Deposit
 * Protection flow.
 *
 * Method name — `wallet_requestExecutionPermissions` (current) vs
 * `wallet_grantPermissions` (legacy)
 * ────────────────────────────────────────────────────────────────
 * ERC-7715 originally defined the request RPC as
 * `wallet_grantPermissions`. The spec was renamed mid-2025 to
 * `wallet_requestExecutionPermissions` (ethereum/ERCs commit
 * 2adc378) and MetaMask shipped the rename in extension PR #39176
 * ("feat: align with updated 7715 standard"). The hook layer
 * dispatches the canonical name first and falls back to the legacy
 * name on -32601 — see `use-grant-protection-permissions.ts` for
 * the full rationale.
 *
 * This module is method-name agnostic: the request shape is
 * identical between the two names (the rename was a method-name
 * rename, not a request schema change), so we keep one builder.
 *
 * Why this module exists
 * ──────────────────────
 * MetaMask intentionally blocks external `eth_signTypedData_v4`
 * delegation requests via `validateDelegation` in
 * `@metamask/core/signature-controller/utils/validation.ts`. The
 * intended path for MM-managed accounts is ERC-7715 — the wallet
 * exposes a high-level RPC that internally produces an ERC-7710
 * delegation matching the same onchain primitive our keeper
 * redeems. From the keeper's point of view, a 7715-granted permission
 * and a 7710-direct delegation are identical: both feed into
 * `DelegationManager.redeemDelegations(...)` via a
 * `(permissionContext, mode, executionCallData)` triple.
 *
 * The catch — and the reason the helper is split out — is that
 * `wallet_grantPermissions` doesn't expose arbitrary enforcer caveats.
 * Per the @metamask/7715-permission-types spec, a request carries
 *
 *   { chainId, from?, to, permission, rules? }
 *
 * where `permission.type` must be one of MM's documented strings
 * (`erc20-token-periodic`, `native-token-periodic`, …) and `rules` is
 * a constrained allowlist (today: just `expiry`). There is no field
 * for a `DepositProtectionEnforcer` caveat. Consequence:
 *
 *   • The 7715 path persists a delegation whose only onchain caveats
 *     are the standard `ERC20PeriodicAllowanceEnforcer` ones MM emits
 *     (one per token), plus an implicit timestamp bound from `expiry`.
 *
 *   • Runway-gate, target-runway, and the allowed-UGM allowlist —
 *     things the 7710 path enforces *onchain* via the
 *     `DepositProtectionEnforcer` caveat — are not enforceable on
 *     chain for 7715 rows. The keeper enforces them server-side using
 *     the metadata stored alongside the row (`runway_gate_seconds`,
 *     `target_runway_seconds`, `allowed_ugms`). The DB row carries a
 *     `signing_path` discriminator so the cron knows which enforcement
 *     model applies.
 *
 *   • Worst case for an MM-protected user is therefore: a malicious
 *     keeper wastes their weekly `periodAmount` cap on legitimate-
 *     looking `addDeposit(...)` calls into UGM seats they own. No
 *     theft, just misallocation; bounded by `periodAmount` and capped
 *     at `expiry`. We surface this trade-off in the dialog copy.
 *
 * Surface
 * ───────
 *   • `buildGrantPermissionsRequest({...})` — builds the array of
 *     `PermissionRequest` objects to pass as `params[0]` to either
 *     `wallet_requestExecutionPermissions` (preferred) or the legacy
 *     `wallet_grantPermissions` (fallback). Pure / no I/O.
 *
 *   • `decodeGrantPermissionsResponse(raw, { ... })` — type-guards
 *     and normalises the wallet's response. Returns one decoded
 *     grant per requested permission, in the same order. Throws on
 *     anything malformed (length mismatch, missing `context`,
 *     wrong token address, …) so the caller can surface an
 *     actionable error instead of persisting a corrupt row.
 *
 * Both helpers stay framework-agnostic. The React wrapper that
 * actually calls the EIP-1193 provider lives in
 * `src/hooks/use-grant-protection-permissions.ts`.
 */

import {
  type Address,
  type Hex,
  getAddress,
  isAddress,
  isHex,
} from 'viem';

import type {
  Erc20TokenPeriodicPermission,
  PermissionRequest,
  PermissionResponse,
} from '@metamask/7715-permission-types';

// ─── Shared constants ────────────────────────────────────────────────

/**
 * Period for the `erc20-token-periodic` permission. We mirror the
 * 7-day rolling-window semantics of the 7710 path's
 * `ERC20PeriodicAllowanceEnforcer` so a user moving between paths
 * doesn't get surprised by a different cap window.
 */
export const PROTECTION_PERIOD_DURATION_SECONDS = 7 * 86400;

/**
 * MM's ERC-7715 spec accepts an "expiry" rule with a Unix timestamp.
 * We always emit it so the granted delegation expires alongside the
 * user-configured expiresAt — the standard timestamp enforcer the
 * 7710 path persists into the caveat list.
 */
export interface ExpiryRule {
  type: 'expiry';
  data: { timestamp: number };
}

// ─── Request builder ─────────────────────────────────────────────────

export interface BuildGrantPermissionsArgs {
  /** Decimal chain id; converted to hex before sending. */
  chainId: number;
  /** Wallet account being delegated FROM (the user's wallet). */
  fromAccount: Address;
  /**
   * Address the granted permission delegates TO. MM requires a
   * concrete address here — the `ANY_DELEGATE = 0xa11` sentinel that
   * the 7710 path uses is rejected. The caller supplies the
   * `ProtectionRedeemer` contract address configured via
   * `NEXT_PUBLIC_PROTECTION_REDEEMER_ADDRESS`. The redeemer's
   * `redeem(...)` is permissionless, so passing the contract address
   * (rather than an EOA) keeps the system decentralised: any caller
   * can submit a batch, the contract enforces the protocol invariants
   * pre-DM.
   */
  sessionAccount: Address;
  /** Unix seconds. Mirrored into `rules: [{ type: 'expiry', ... }]`. */
  expiresAt: number;
  /** Weekly USDC cap (raw 6-decimal units). 0 = no USDC permission. */
  weeklyUsdcCap: bigint;
  /** Weekly TAKEOVER cap (raw 18-decimal units). 0 = no TAKEOVER permission. */
  weeklyTakeoverCap: bigint;
  usdcAddress: Address;
  takeoverAddress: Address;
  /**
   * Whether MM should let the user adjust the requested cap in its UI.
   * The `BasePermission` schema requires this field be a boolean —
   * defaulting to `false` keeps the cap exactly what the dialog
   * showed the user.
   */
  isAdjustmentAllowed?: boolean;
  /**
   * Optional human-readable string MM displays in the consent UI.
   * Falls back to a generic Deposit Protection blurb.
   */
  justification?: string;
}

/** Per-token permission descriptor — the granular shape the wallet sees. */
export type ProtectionPermissionRequest = PermissionRequest<Erc20TokenPeriodicPermission>;

function bigintToHex(n: bigint): Hex {
  // The MM toolkit accepts unprefixed 0x for any non-negative bigint.
  // We never need negative values here (caps are always >= 0).
  if (n < 0n) throw new Error('cap must be non-negative');
  return `0x${n.toString(16)}` as Hex;
}

/**
 * Build the 7715 params array. Output is one `PermissionRequest`
 * per token with a cap > 0 (USDC first, then TAKEOVER, when both are
 * present) targeting MM's `erc20-token-periodic` permission type with
 * a 7-day `periodDuration` and an `expiry` rule pinned at `expiresAt`.
 * A holder whose protectable seats are all TAKEOVER-taxed (e.g. the
 * PotWars grid) legitimately has `weeklyUsdcCap === 0n` — we skip the
 * USDC permission entirely rather than asking MM for a zero cap.
 * Shared between the canonical `wallet_requestExecutionPermissions`
 * call and the legacy `wallet_grantPermissions` fallback (same
 * request schema).
 *
 * Validation goals — fail fast at build time so a misconfigured client
 * can't burn a wallet prompt and end up with nothing usable persisted:
 *
 *   - At least one weekly cap must be > 0 (the keeper has nothing to
 *     redeem against if both caps are zero) — same rule the server
 *     route applies in `enable-validation.ts`.
 *   - `expiresAt` must be in the future and within 1 year — same
 *     bounds the server-side route applies, mirrored client-side so
 *     we surface them before the user sees an MM rejection.
 *   - Every `Address` field must round-trip through `getAddress` so
 *     the wallet doesn't reject on checksum mismatches.
 */
export function buildGrantPermissionsRequest(
  args: BuildGrantPermissionsArgs,
): ProtectionPermissionRequest[] {
  if (!Number.isInteger(args.chainId) || args.chainId <= 0) {
    throw new Error('chainId must be a positive integer');
  }
  if (!isAddress(args.fromAccount)) {
    throw new Error('fromAccount must be an address');
  }
  if (!isAddress(args.sessionAccount)) {
    throw new Error('sessionAccount must be an address');
  }
  if (!isAddress(args.usdcAddress) || !isAddress(args.takeoverAddress)) {
    throw new Error('usdcAddress and takeoverAddress must be addresses');
  }
  const now = Math.floor(Date.now() / 1000);
  if (!Number.isInteger(args.expiresAt) || args.expiresAt <= now) {
    throw new Error('expiresAt must be a future Unix timestamp (seconds)');
  }
  const oneYearFromNow = now + 365 * 86400;
  if (args.expiresAt > oneYearFromNow) {
    throw new Error('expiresAt must be within 1 year');
  }
  if (args.weeklyUsdcCap < 0n) {
    throw new Error('weeklyUsdcCap must be >= 0');
  }
  if (args.weeklyTakeoverCap < 0n) {
    throw new Error('weeklyTakeoverCap must be >= 0');
  }
  if (args.weeklyUsdcCap === 0n && args.weeklyTakeoverCap === 0n) {
    throw new Error('at least one weekly cap must be > 0');
  }

  const chainHex = `0x${args.chainId.toString(16)}` as Hex;
  const rules: ExpiryRule[] = [
    { type: 'expiry', data: { timestamp: args.expiresAt } },
  ];

  const justification =
    args.justification ??
    'Takeover Land Protection — top up tile deposits that drop near runway';

  function buildOne(token: Address, cap: bigint): ProtectionPermissionRequest {
    return {
      chainId: chainHex,
      from: getAddress(args.fromAccount),
      to: getAddress(args.sessionAccount),
      permission: {
        type: 'erc20-token-periodic',
        isAdjustmentAllowed: args.isAdjustmentAllowed ?? false,
        data: {
          tokenAddress: getAddress(token),
          periodAmount: bigintToHex(cap),
          periodDuration: PROTECTION_PERIOD_DURATION_SECONDS,
          startTime: now,
          justification,
        },
      },
      rules,
    };
  }

  const requests: ProtectionPermissionRequest[] = [];
  if (args.weeklyUsdcCap > 0n) {
    requests.push(buildOne(args.usdcAddress, args.weeklyUsdcCap));
  }
  if (args.weeklyTakeoverCap > 0n) {
    requests.push(buildOne(args.takeoverAddress, args.weeklyTakeoverCap));
  }
  return requests;
}

// ─── Response decoder ────────────────────────────────────────────────

export interface DecodedPermissionGrant {
  /** `permissionContext` — feed into `DelegationManager.redeemDelegations`. */
  permissionContext: Hex;
  /** ERC-7710 manager that processes redemptions for this grant. */
  delegationManager: Address;
  /** Token the periodic permission targets (echoed back from the request). */
  tokenAddress: Address;
  /** Weekly cap MM actually granted (may differ from the request when
   *  `isAdjustmentAllowed === true`). */
  periodAmount: bigint;
  /** Period in seconds (we always request 7 days; pinned client-side). */
  periodDuration: number;
  /** Expiry rule echoed back, if MM honoured ours. Null means MM dropped it. */
  expiresAt: number | null;
  /**
   * Factory deploys MM may need to run ahead of the first redemption
   * (when the granter is an undeployed Smart Account). The keeper
   * concatenates these into the redemption tx; we persist them
   * alongside the row.
   */
  dependencies: Array<{ factory: Address; factoryData: Hex }>;
}

export interface DecodeGrantPermissionsArgs {
  /**
   * Echoed-back token addresses we expect the wallet to honour, in
   * request order (USDC first, then TAKEOVER, when both were
   * requested). Each is present iff the corresponding cap was > 0 at
   * request time; at least one must be set.
   */
  expected: { usdc?: Address; takeover?: Address };
}

interface MaybeResponse {
  permission?: { type?: unknown; data?: unknown };
  context?: unknown;
  delegationManager?: unknown;
  dependencies?: unknown;
  rules?: unknown;
  signerMeta?: { delegationManager?: unknown };
}

function readDelegationManager(raw: MaybeResponse): Address {
  // The current `@metamask/7715-permission-types` shape exposes
  // `delegationManager` flat on the response. Older MM toolkit
  // releases nested it under `signerMeta.delegationManager`. Accept
  // both, prefer the flat shape (the spec).
  const flat =
    typeof raw.delegationManager === 'string' ? raw.delegationManager : undefined;
  const nested =
    raw.signerMeta && typeof raw.signerMeta.delegationManager === 'string'
      ? raw.signerMeta.delegationManager
      : undefined;
  const v = flat ?? nested;
  if (!v || !isAddress(v)) {
    throw new Error('grant response missing delegationManager address');
  }
  return getAddress(v);
}

function readDependencies(raw: MaybeResponse): Array<{ factory: Address; factoryData: Hex }> {
  if (raw.dependencies === undefined || raw.dependencies === null) return [];
  if (!Array.isArray(raw.dependencies)) {
    throw new Error('grant response.dependencies must be an array');
  }
  return raw.dependencies.map((entry, i) => {
    if (entry === null || typeof entry !== 'object') {
      throw new Error(`grant response.dependencies[${i}] must be an object`);
    }
    const e = entry as { factory?: unknown; factoryData?: unknown };
    if (typeof e.factory !== 'string' || !isAddress(e.factory)) {
      throw new Error(`grant response.dependencies[${i}].factory invalid`);
    }
    if (typeof e.factoryData !== 'string' || !isHex(e.factoryData)) {
      throw new Error(`grant response.dependencies[${i}].factoryData invalid`);
    }
    return { factory: getAddress(e.factory), factoryData: e.factoryData };
  });
}

function readExpiry(raw: MaybeResponse): number | null {
  // Per spec, `rules` is echoed back. MM may drop unsupported rules,
  // in which case the array is empty / undefined. Treat that as
  // "indefinite" (the onchain delegation will still be bounded by
  // `expiry` semantics if the wallet honoured it internally; if it
  // didn't, the delegation is unbounded which we surface as `null`).
  if (!Array.isArray(raw.rules)) return null;
  const expiryRule = (raw.rules as Array<{ type?: unknown; data?: unknown }>).find(
    (r) => r && typeof r === 'object' && r.type === 'expiry',
  );
  if (!expiryRule) return null;
  const data = expiryRule.data as { timestamp?: unknown } | undefined;
  if (!data || typeof data.timestamp !== 'number' || !Number.isFinite(data.timestamp)) {
    return null;
  }
  return Math.floor(data.timestamp);
}

function readPeriodicData(
  raw: MaybeResponse,
  index: number,
): { tokenAddress: Address; periodAmount: bigint; periodDuration: number } {
  if (
    !raw.permission ||
    typeof raw.permission !== 'object' ||
    raw.permission.type !== 'erc20-token-periodic'
  ) {
    throw new Error(`grant response[${index}].permission.type must be 'erc20-token-periodic'`);
  }
  const data = raw.permission.data as
    | {
        tokenAddress?: unknown;
        periodAmount?: unknown;
        periodDuration?: unknown;
      }
    | undefined;
  if (!data || typeof data !== 'object') {
    throw new Error(`grant response[${index}].permission.data missing`);
  }
  if (typeof data.tokenAddress !== 'string' || !isAddress(data.tokenAddress)) {
    throw new Error(`grant response[${index}].permission.data.tokenAddress invalid`);
  }
  if (typeof data.periodAmount !== 'string' || !isHex(data.periodAmount)) {
    throw new Error(`grant response[${index}].permission.data.periodAmount must be hex`);
  }
  if (
    typeof data.periodDuration !== 'number' ||
    !Number.isInteger(data.periodDuration) ||
    data.periodDuration <= 0
  ) {
    throw new Error(`grant response[${index}].permission.data.periodDuration invalid`);
  }
  return {
    tokenAddress: getAddress(data.tokenAddress),
    periodAmount: BigInt(data.periodAmount),
    periodDuration: data.periodDuration,
  };
}

/**
 * Decode the wallet's 7715 response into our canonical
 * `DecodedPermissionGrant[]`. Schema is identical between the
 * canonical (`wallet_requestExecutionPermissions`) and legacy
 * (`wallet_grantPermissions`) RPC names — only the method name
 * changed in the rename.
 *
 * The wallet returns one response per requested permission, in the
 * same order. We assert that order against the requested tokens to
 * catch a buggy wallet that silently reorders (which would otherwise
 * cause us to persist mismatched caps).
 */
export function decodeGrantPermissionsResponse(
  raw: unknown,
  args: DecodeGrantPermissionsArgs,
): DecodedPermissionGrant[] {
  if (!Array.isArray(raw)) {
    throw new Error('grant response must be an array');
  }
  if (raw.length === 0) {
    throw new Error('grant response must contain at least one permission');
  }

  // Request order: USDC first iff requested, TAKEOVER second iff
  // requested. The builder only emits permissions for tokens with a
  // cap > 0, so a TAKEOVER-only grant legitimately has no USDC entry.
  const expectedOrder: Address[] = [];
  if (args.expected.usdc) expectedOrder.push(getAddress(args.expected.usdc));
  if (args.expected.takeover) expectedOrder.push(getAddress(args.expected.takeover));
  if (expectedOrder.length === 0) {
    throw new Error('decodeGrantPermissionsResponse: no expected tokens provided');
  }
  if (raw.length !== expectedOrder.length) {
    throw new Error(
      `grant response length mismatch — expected ${expectedOrder.length}, got ${raw.length}`,
    );
  }

  return raw.map((entryRaw, i) => {
    if (!entryRaw || typeof entryRaw !== 'object') {
      throw new Error(`grant response[${i}] must be an object`);
    }
    const entry = entryRaw as MaybeResponse;
    if (typeof entry.context !== 'string' || !isHex(entry.context)) {
      throw new Error(`grant response[${i}].context must be hex`);
    }
    const { tokenAddress, periodAmount, periodDuration } = readPeriodicData(entry, i);
    if (tokenAddress.toLowerCase() !== expectedOrder[i].toLowerCase()) {
      throw new Error(
        `grant response[${i}].tokenAddress (${tokenAddress}) does not match expected ${expectedOrder[i]}`,
      );
    }
    return {
      permissionContext: entry.context,
      delegationManager: readDelegationManager(entry),
      tokenAddress,
      periodAmount,
      periodDuration,
      expiresAt: readExpiry(entry),
      dependencies: readDependencies(entry),
    };
  });
}

/**
 * Convenience: type-guard for an EIP-1193-style provider error so
 * the hook layer can present a clean toast for the most common
 * MetaMask outcomes (user rejected, MM doesn't support 7715, etc).
 */
export function isProviderError(e: unknown): e is { code: number; message: string } {
  return (
    typeof e === 'object' &&
    e !== null &&
    typeof (e as { code?: unknown }).code === 'number' &&
    typeof (e as { message?: unknown }).message === 'string'
  );
}

/** Re-export so consumers don't have to depend on the MM types package. */
export type { Erc20TokenPeriodicPermission, PermissionRequest, PermissionResponse };
